import React, { ComponentProps, useContext, useEffect } from 'react';

import {
  ActivityCard,
  ActivityCardLoader,
} from '@adsk/alloy-react-activity-log';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';

import ReferenceActivityCardError from './activity/ReferenceActivityCardError';
import ReferenceActivityCardNoPermission from './activity/ReferenceActivityCardNoPermission';
import { getOrCreatePluginContext } from './helpers/pluginContexts';
import { useTranslation } from './i18n';
import { useEntitiesCacheContext } from './providers/EntitiesCacheProvider';
import { useReferencePickerPlugins } from './providers/ReferencePickerPluginsProvider';
import { CommonReferencePickerPlugin } from './types/ReferencePickerPlugin';
import { EntityWithId } from './types/types';

const ACTIVITY_VARIANTS = {
  ADDED: 'added',
  REMOVED: 'removed',
};

type ConnectedReferenceActivityCardProps = ComponentProps<
  typeof ActivityCard
> & {
  plugins?: CommonReferencePickerPlugin[];
  entityId: string;
  entityDomain: string;
  entityType: string;
  title?: string;
  variant?: string;
};

interface PluginContextProps {
  fetchByIds?: (ids: string[]) => void;
}

/**
 * A connected reference activity card component.
 *
 * This component allows you to render an activity card for a specific reference change.
 *
 * This component expects `ReferencePickerProvider` somewhere above it in the components tree.
 */
const ConnectedReferenceActivityCard = React.forwardRef<
  HTMLDivElement,
  ConnectedReferenceActivityCardProps
>(
  (
    {
      plugins: pluginsFromProp,
      entityId,
      entityType,
      entityDomain,
      title: titleProp,
      variant = ACTIVITY_VARIANTS.ADDED,
      ...props
    },
    ref,
  ) => {
    const { allowedPlugins } = useReferencePickerPlugins();
    const pluginsToUse = pluginsFromProp || allowedPlugins;
    const plugin = pluginsToUse
      .filter(Boolean)
      .find(
        (p) =>
          p?.metadata?.domain === entityDomain &&
          p?.metadata?.type === entityType,
      );
    const PluginContext = plugin
      ? (getOrCreatePluginContext(plugin) as React.Context<PluginContextProps>)
      : React.createContext<PluginContextProps>({});
    const { entitiesCache } = useEntitiesCacheContext();
    const contextValue = useContext<PluginContextProps>(PluginContext);
    const fetchByIds = contextValue?.fetchByIds;
    const { t } = useTranslation();
    const title =
      titleProp ||
      (variant === ACTIVITY_VARIANTS.REMOVED
        ? t('ACTIVITY_TITLE_REMOVED')
        : t('ACTIVITY_TITLE_ADDED'));

    useEffect(() => {
      if (entityId && fetchByIds) {
        fetchByIds([entityId]);
      }
    }, [entityId, fetchByIds]);

    const { ActivityCard, NoPermissionActivityCard } = plugin || {};
    if (!ActivityCard) {
      return null;
    }
    const entityData =
      entitiesCache?.[entityDomain]?.[entityType]?.[entityId] ?? {};
    if (entityData?.loading) {
      return <ActivityCardLoader title={title} {...props} />;
    }
    if (entityData?.error) {
      return (
        <ReferenceActivityCardError
          onTryAgain={entityData?.retry}
          title={title}
          {...props}
        />
      );
    }

    if (entityData?.entity) {
      return (
        <ActivityCard
          ref={ref}
          title={title}
          entity={entityData.entity}
          variant={variant}
          {...props}
        />
      );
    }

    const NoPermissionComponent =
      NoPermissionActivityCard || ReferenceActivityCardNoPermission;
    return (
      <NoPermissionComponent
        entity={entityData.entity as EntityWithId}
        ref={ref}
        title={title}
        variant={variant}
        {...props}
      />
    );
  },
);

ConnectedReferenceActivityCard.displayName = 'ConnectedReferenceActivityCard';

const ConnectedReferenceActivityCardWithStatic = Object.assign(
  WithAlloySuspense(ConnectedReferenceActivityCard),
  {
    VARIANTS: ACTIVITY_VARIANTS,
  },
);

export default ConnectedReferenceActivityCardWithStatic;
